import { useMemo } from 'react';

import {
  adminPharmaciesInfo,
  IAdminWorkshiftDetails,
  ProfessionalReplacementTypes,
} from '@pharmaplan/common';

import { useAppSelector } from '../useAppSelector';

const useHourlyRates = ({ details, isFromCalendar }:{details?: IAdminWorkshiftDetails, isFromCalendar?:boolean}) => {
  const pharmaciesInfo = useAppSelector(adminPharmaciesInfo);
  const { pharmacistHourlyRate: savedPharmacistRate } = details ?? {};

  const {
    hourlyRate,
    pharmacyHourlyRate,
    pharmacistHourlyRate,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    technicianHourlyRate,
    studentHourlyRate,
    pharmacistWeekDayHourlyRate,
    professionalWeekDayHourlyRate,
    professionalWeekendHourlyRate,
    pharmacistWeekendHourlyRate,
    technicianWeekDayHourlyRate,
    technicianWeekendHourlyRate,
    studentWeekDayHourlyRate,
    studentWeekendHourlyRate,
    pharmacyHourlyRateForTech,
    pharmacyHourlyRateForStudent,
    pharmacyWeekDayHourlyRateForTech,
    pharmacyWeekendHourlyRateForTech,
    pharmacyWeekDayHourlyRateForStudent,
    pharmacyWeekendHourlyRateForStudent,
  } = pharmaciesInfo ?? {};

  const userRate = useMemo(() => {
    if (!isFromCalendar) {
      return {
        [ProfessionalReplacementTypes.technician]: savedPharmacistRate,
        [ProfessionalReplacementTypes.pharmacist]:
          savedPharmacistRate,
        [ProfessionalReplacementTypes.student]: savedPharmacistRate,
      };
    }

    return {
      [ProfessionalReplacementTypes.technician]: technicianHourlyRate,
      [ProfessionalReplacementTypes.pharmacist]:
          pharmacistHourlyRate || hourlyRate,
      [ProfessionalReplacementTypes.student]: studentHourlyRate,
    };
  }, [
    isFromCalendar,
    technicianHourlyRate,
    pharmacistHourlyRate,
    hourlyRate,
    studentHourlyRate,
    savedPharmacistRate,
  ]);

  const pharmacyUserRate = useMemo(
    () =>
      ({
        [ProfessionalReplacementTypes.technician]: pharmacyHourlyRateForTech,
        [ProfessionalReplacementTypes.pharmacist]: pharmacyHourlyRate,
        [ProfessionalReplacementTypes.student]: pharmacyHourlyRateForStudent,
      }),
    [
      pharmacyHourlyRate,
      pharmacyHourlyRateForStudent,
      pharmacyHourlyRateForTech,
    ],
  );

  const pharmacyUserMDSRate = useMemo(
    () =>
      ({
        [ProfessionalReplacementTypes.technician]: {
          weekday: pharmacyWeekDayHourlyRateForTech,
          weekend: pharmacyWeekendHourlyRateForTech,
        },
        [ProfessionalReplacementTypes.pharmacist]: {
          weekday: pharmacyWeekDayHourlyRate,
          weekend: pharmacyWeekendHourlyRate,
        },
        [ProfessionalReplacementTypes.student]: {
          weekday: pharmacyWeekDayHourlyRateForStudent,
          weekend: pharmacyWeekendHourlyRateForStudent,
        },
      }),
    [
      pharmacyWeekDayHourlyRate,
      pharmacyWeekDayHourlyRateForStudent,
      pharmacyWeekDayHourlyRateForTech,
      pharmacyWeekendHourlyRate,
      pharmacyWeekendHourlyRateForStudent,
      pharmacyWeekendHourlyRateForTech,
    ],
  );

  const userMDSRate = useMemo(
    () =>
      ({
        [ProfessionalReplacementTypes.technician]: {
          weekday: technicianWeekDayHourlyRate,
          weekend: technicianWeekendHourlyRate,
        },
        [ProfessionalReplacementTypes.pharmacist]: {
          weekday: professionalWeekDayHourlyRate || pharmacistWeekDayHourlyRate,
          weekend: professionalWeekendHourlyRate || pharmacistWeekendHourlyRate,
        },
        [ProfessionalReplacementTypes.student]: {
          weekday: studentWeekDayHourlyRate,
          weekend: studentWeekendHourlyRate,
        },
      }),
    [
      technicianWeekDayHourlyRate,
      technicianWeekendHourlyRate,
      professionalWeekDayHourlyRate,
      pharmacistWeekDayHourlyRate,
      professionalWeekendHourlyRate,
      pharmacistWeekendHourlyRate,
      studentWeekDayHourlyRate,
      studentWeekendHourlyRate,
    ],
  );

  return { userMDSRate, userRate, pharmacyUserMDSRate, pharmacyUserRate };
};

export default useHourlyRates;
